import { useEffect, useRef } from "react";
import useItemHandler from "../PositionedHooks/useItemHandler";
import { usePositionedScreenContext } from "../PositionedScreen";
import {
  adjustSectionHeights,
  alignItemsToLeft,
  alignItemsToTop,
  checkItemsTypeUniformity,
  distributeItemsHorizontally,
  distributeItemsVertically,
  findItemById,
  getValueByPath,
  moveMultiselectedItems,
  moveSelectedItem,
  multiDuplicateElements,
  resizeSelectedItem,
  updateSelectedItemsPosition
} from "../utils";
import { keyToDirection } from "./keyboardHandlerUtils";
import { showGuidelineForElementMove } from "../Guidelines/guidelinesFunc";
import { toastError } from "../../../utils/toastUtils";
import useAIConfigModal, { AIConfigModal } from "../PositionedHooks/useAIConfigModal";

const KeyboardHandler = ({ containerRef, screenWidth }) => {
  const {
    layoutConfig,
    selectedItem,
    setSelectedItem,
    setLayoutConfig,
    gridSize,
    onClickElement,
    type,
    selectedItemPath,
    setSelectedItemPath,
    multiSelectedItems,
    setMultiSelectedItems,
    elementRefs,
    setGuidlineData,
    closeEditBar,
    openMultiFieldEditBar
  } = usePositionedScreenContext();

  const guidelineTimeoutRef = useRef(null);
  const { duplicateElement, copyElement, pasteElement, cutElement, deleteElement, deleteMultiSelectedItems } =
    useItemHandler({
      layoutConfig,
      setLayoutConfig,
      setSelectedItem,
      setSelectedItemPath,
      setMultiSelectedItems,
      type,
      gridSize
    });
  const adjustRef = useRef();

  const {
    isAiConfigModalOpen,
    setIsAiConfigModalOpen,
    aiConfig,
    setAiConfig,
    handleAIConfigConfirm,
    openAIConfigModal
  } = useAIConfigModal({ layoutConfig, setLayoutConfig });

  useEffect(() => {
    adjustRef.current = createAdjustHeightScheduler(setLayoutConfig);
  }, [setLayoutConfig]);
  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      if (e.key === "Escape") {
        setSelectedItem(null);
        setSelectedItemPath(null);
        setMultiSelectedItems(null);
        closeEditBar();
        return;
      }
      const isTypingField =
        activeTag === "input" || activeTag === "textarea" || document.activeElement?.isContentEditable;
      if (isTypingField) return;
      // if (e.ctrlKey && e.key === "g") {
      //   console.log("start calling");
      //   const newLayout = adjustHeightsWithGrid(layoutConfig, gridSize);
      //   console.log("newLayout", newLayout);
      //   setLayoutConfig(newLayout);
      //   return;
      // }
      if (multiSelectedItems) {
        if (type !== "editable") return;
        if (e.shiftKey) {
          switch (e.key) {
            case "Delete":
            case "Backspace":
              deleteMultiSelectedItems(multiSelectedItems);
              break;
            default:
              break;
          }
        } else {
          switch (e.key) {
            case "ArrowUp":
            case "ArrowDown":
            case "ArrowLeft":
            case "ArrowRight":
              let { newLayoutConfig, updatedMultiSelectedItems } = moveMultiselectedItems(
                layoutConfig,
                multiSelectedItems,
                keyToDirection[e.key],
                gridSize
              );
              setLayoutConfig(newLayoutConfig);
              setMultiSelectedItems(updatedMultiSelectedItems);
              e.preventDefault();
              break;
            case "l":
              e.preventDefault();
              const leftLayout = alignItemsToLeft(layoutConfig, multiSelectedItems);
              const leftUpdatedItems = updateSelectedItemsPosition(multiSelectedItems, leftLayout, "left");
              setLayoutConfig(leftLayout);
              setMultiSelectedItems(leftUpdatedItems);
              break;
            case "t":
              e.preventDefault();
              const topLayout = alignItemsToTop(layoutConfig, multiSelectedItems);
              const topUpdatedItems = updateSelectedItemsPosition(multiSelectedItems, topLayout, "top");
              setLayoutConfig(topLayout);
              setMultiSelectedItems(topUpdatedItems);
              break;
            case "v":
              e.preventDefault();
              const verticalLayout = distributeItemsVertically(layoutConfig, multiSelectedItems);
              const updatedItems = updateSelectedItemsPosition(multiSelectedItems, verticalLayout, "top");
              setLayoutConfig(verticalLayout);
              setMultiSelectedItems(updatedItems);
              break;
            case "h":
              e.preventDefault();
              const horizontalLayout = distributeItemsHorizontally(layoutConfig, multiSelectedItems);
              const horizontalUpdatedItems = updateSelectedItemsPosition(multiSelectedItems, horizontalLayout, "left");
              setLayoutConfig(horizontalLayout);
              setMultiSelectedItems(horizontalUpdatedItems);
              break;
          }
        }
        if (e.ctrlKey && e.key === "d") {
          const { layout: n, items } = multiDuplicateElements(layoutConfig, multiSelectedItems, gridSize);
          setLayoutConfig(n);
          setMultiSelectedItems(items);
          // duplicateElement(element, { path: selectedItemPath });
          return;
        }
        if (e.ctrlKey && e.key === "c") {
          copyElement(multiSelectedItems, "multi_positioned_element");
          return;
        }
        if (e.ctrlKey && e.key === "e") {
          const { allSameType, type } = checkItemsTypeUniformity(multiSelectedItems);
          if (allSameType) {
            openMultiFieldEditBar(type, multiSelectedItems);
          } else {
            toastError("Please Select Same Type Elements");
          }
          return;
        }
      }

      if (!selectedItem) return;
      let element = findItemById(layoutConfig, selectedItem.id);
      if (!element && selectedItemPath) {
        const mainElement = findItemById(layoutConfig, selectedItemPath[0]);
        if (mainElement) {
          const data = getValueByPath(mainElement, selectedItemPath[1]);
          element = findItemById(data, selectedItem.id);
        }
      }
      if (!element) return;
      if (type === "template_selector") {
        if (e.ctrlKey && e.key === "c") {
          copyElement(element);
          return;
        }
      }
      if (type !== "editable") return;
      if (e.ctrlKey && e.key === "e") {
        onClickElement(element);
        return;
      }
      if (e.ctrlKey && e.key === "d") {
        duplicateElement(element, { path: selectedItemPath });
        return;
      }
      if (e.ctrlKey && e.key === "c") {
        copyElement(element);
        return;
      }
      if (e.ctrlKey && e.key === "v") {
        pasteElement(element, { path: selectedItemPath });
        return;
      }
      if (e.ctrlKey && e.key === "x") {
        cutElement(element, { path: selectedItemPath });
        return;
      }

      if (e.ctrlKey && e.key === "a") {
        e.preventDefault();
        if (element.type === "section" || element.type === "column" || element.type === "block") {
          openAIConfigModal();
        }
        return;
      }

      const isSection = element.type === "section";
      const isColumn = element.type === "column";
      let updatedLayout;

      if (e.shiftKey) {
        switch (e.key) {
          case "Delete":
          case "Backspace":
            deleteElement(element, { path: selectedItemPath });
            e.preventDefault();
            break;

          case "ArrowUp":
          case "ArrowDown":
          case "ArrowLeft":
          case "ArrowRight": {
            const isVertical = e.key === "ArrowUp" || e.key === "ArrowDown";
            const direction =
              e.key === "ArrowUp" ? "up" : e.key === "ArrowDown" ? "down" : e.key === "ArrowLeft" ? "left" : "right";

            const shouldResize = isVertical ? isSection || !isColumn : isColumn || !isSection;

            if (shouldResize) {
              const updatedLayout = resizeSelectedItem(layoutConfig, element, direction, gridSize, {
                path: selectedItemPath
              });
              setLayoutConfig(updatedLayout);
              showGuidelineForElementMove(element, e.key, true, {
                containerRef,
                elementRefs,
                gridSize,
                screenWidth,
                setGuidlineData,
                guidelineTimeoutRef
              });
              adjustRef.current();
              e.preventDefault();
              // setLayoutConfig((prev) => adjustSectionHeights(prev));
            }
            break;
          }
          default:
            return;
        }
      } else {
        switch (e.key) {
          case "ArrowUp":
          case "ArrowDown":
          case "ArrowLeft":
          case "ArrowRight":
            updatedLayout = moveSelectedItem(layoutConfig, element, keyToDirection[e.key], gridSize, {
              path: selectedItemPath
            });
            setLayoutConfig(updatedLayout);
            showGuidelineForElementMove(element, e.key, false, {
              containerRef,
              elementRefs,
              gridSize,
              screenWidth,
              setGuidlineData,
              guidelineTimeoutRef
            });
            adjustRef.current();
            e.preventDefault();

            break;
          default:
            return;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutConfig, selectedItem, multiSelectedItems, setLayoutConfig, gridSize, selectedItemPath]);

  return (
    <AIConfigModal
      isOpen={isAiConfigModalOpen}
      onClose={() => {
        setIsAiConfigModalOpen(false);
        setAiConfig(null);
      }}
      aiConfig={aiConfig}
      setAiConfig={setAiConfig}
      handleConfirm={handleAIConfigConfirm}
      selectedItem={selectedItem}
    />
  );
  // return null;
};

function createAdjustHeightScheduler(setLayoutConfig, delay = 500) {
  let timeoutId = null;

  return () => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      setLayoutConfig((prev) => adjustSectionHeights(prev));
    }, delay);
  };
}

export default KeyboardHandler;
